import { LocalCache } from "../../jsx/cache/cache";
import { AuthBase } from "../../plunk/service/auth-base";
import { parseClaims, boolClaim, productClaim } from "../auth/cognito";
import { getSsoTenant, getSsoTenantConfig } from "../auth/sso";
function appsFrom(claims) {
  const groups = (claims && claims['cognito:groups'] || []).map(x => String(x).toLowerCase());
  if (groups.includes('administrator') || groups.includes('root')) return ['studio', 'app'];
  const list = String(claims && claims['custom:apps'] || '').split(',').map(x => x.trim().toLowerCase()).map(x => x === 'apps' ? 'app' : x).filter(x => x === 'studio' || x === 'app');
  return list.length ? [...new Set(list)] : ['studio', 'app'];
}

// Section access (WORKSPACE_SECTION_ROLES.md, 2026-10-23): a member sees the Sales or
// Marketing section only with the matching Cognito group; owners, administrators and
// root see everything. Set on account.plunk.io › Members. One helper for the nav, the
// route guard and the Dashboard catalog; the services enforce the same rule.
export const PRIVILEGED_GROUPS = ['owner', 'administrator', 'root'];
export function hasSection(name, groups) {
  const g = (groups || []).map(x => String(x).toLowerCase());
  return g.some(x => PRIVILEGED_GROUPS.indexOf(x) !== -1) || g.indexOf(String(name).toLowerCase()) !== -1;
}

// account.plunk.io (members, billing). Staging opens the staging copy of the page.
export function accountUrl(tab) {
  const staging = typeof window !== 'undefined' && /staging|localhost/.test(window.location.hostname);
  return 'https://account.plunk.io/' + (staging ? 'staging/' : '') + '?from=analytics' + (tab ? '&tab=' + tab : '');
}

// Account info now derives from the Cognito ID token's custom claims.
// Cached on read to avoid re-parsing the JWT for every call site.
// Legacy LocalCache fallback ('plunk-tenant') is kept so the app can still
// boot during the migration when no JWT is present (dev / sign-in pages).

let _cache = null;
let _cacheToken = null;
function fromClaims(claims) {
  if (!claims) return null;
  // DECISION (2026-07-06): this app is bound to the TOKEN's tenant claim.
  // The core data APIs (/v1/location, /v1/activity, ...) scope data by the
  // JWT's tenant_id claim and ignore x-app-id, so honoring the broker's
  // picked tenant here made the UI lie (Fairfield label, home-tenant
  // data — the MI bug). Cross-tenant admin work uses per-tenant admin
  // accounts instead. The broker tenant (getSsoTenant) is deliberately
  // NOT consulted; the sso.js plumbing is kept for a possible future
  // platform-wide acting-tenant model.
  const tenantId = claims['custom:account'] || claims.tenant_id;
  if (!tenantId) return null;

  // Per-tenant config from the auth broker's registry (PlunkTenantConfig).
  // Authoritative for domain/company/label; falls back to Cognito custom
  // claims when absent (registry not yet populated for a tenant).
  const cfg = getSsoTenantConfig();
  const cfgForTenant = cfg && cfg.tenantId === tenantId ? cfg : null;
  const product = productClaim(claims) || {
    url: '/Product/Detail/:sku/:vendor/:name',
    hasName: true,
    hasSku: true
  };
  return {
    tenantId,
    account: claims['custom:account_name'] || claims.account_name || '',
    company: cfgForTenant && cfgForTenant.company || claims['custom:company'] || claims.company || '',
    domain: cfgForTenant && cfgForTenant.domain || claims['custom:domain'] || claims.domain || '',
    whiteLabel: boolClaim(claims, 'custom:white_label') || boolClaim(claims, 'white_label'),
    showCampaigns: boolClaim(claims, 'custom:show_campaigns') || boolClaim(claims, 'show_campaigns'),
    accountOwner: boolClaim(claims, 'custom:account_owner'),
    accountStatus: claims['custom:account_status'] || '',
    groups: claims['cognito:groups'] || [],
    // Per-app access (custom:apps): absent/empty → both; administrator/
    // root → both. Mirrors authenticate.js appAccessAllowed / the broker.
    apps: appsFrom(claims),
    email: claims.email || '',
    sub: claims.sub || '',
    product,
    // SKU regex format isn't a Cognito claim today. Hardcode the known
    // tenant-specific value for RobbStucky; other tenants can be added
    // as needed (or moved into a custom Cognito attribute later).
    skuFormat: tenantId === 'anbsezhvanaj' ? ['^\\d{3}-\\d{4}-\\d{3}-\\d{2}$'] : undefined
  };
}
export default class {
  static get() {
    const idToken = AuthBase.idToken;

    // Token-aware cache: invalidate when the token rotates.
    if (idToken && idToken === _cacheToken && _cache) return _cache;
    if (idToken) {
      const claims = parseClaims(idToken);
      const derived = fromClaims(claims);
      if (derived) {
        _cache = derived;
        _cacheToken = idToken;
        return derived;
      }
    }

    // Fallback when there's no token yet (only briefly — the app adopts
    // the SSO session before first render). Deliberately NEUTRAL, not the
    // old hardcoded Robb & Stucky demo default, which flashed the wrong
    // tenant in the sidebar (whiteLabel + company) until a manual refresh.
    // whiteLabel:false → the sidebar shows the generic "PLUNK" logo, never
    // another tenant's name.
    var key = 'plunk-tenant';
    var value = LocalCache.get(key);
    if (!value) {
      value = {
        tenantId: '',
        account: '',
        company: '',
        domain: '',
        whiteLabel: false,
        showCampaigns: false,
        groups: [],
        product: {
          url: '/Product/Detail/:sku/:vendor/:name',
          hasName: true,
          hasSku: true
        }
      };
    }
    return value;
  }
  static set(value) {
    var key = 'plunk-tenant';
    LocalCache.set(key, value);
  }
  static getEmail() {
    // Prefer the email claim on the live ID token over the cached one.
    const idToken = AuthBase.idToken;
    if (idToken) {
      const claims = parseClaims(idToken);
      if (claims && claims.email) return claims.email;
    }
    var key = 'authTokens';
    var value = LocalCache.get(key);
    if (value) return value.email;
    return null;
  }
  static inGroup(name) {
    const a = this.get();
    if (!a || !Array.isArray(a.groups)) return false;
    return a.groups.indexOf(name) !== -1;
  }
}